import { allRoles, UserRole } from "@/features/auth";

export const apiDomains = ["regievoerder", "klantenservice", "support-consultant", "alleen-lezen", "rcb/dewit", "rcb/transvision"] as const;
export type ApiDomain = (typeof apiDomains)[number];

/**
 * ApiDomain → UserRole lookup. Inverse of `getPrefixFromRole`. Single
 * source of truth for the role↔domain coupling so we don't have to keep two
 * tables in sync (the realtime layer also needs this direction).
 */
export const ROLE_BY_API_DOMAIN = {
    regievoerder: UserRole.REGIEVOERDER,
    klantenservice: UserRole.KLANTENSERVICE,
    "alleen-lezen": UserRole.ALLEENLEZEN,
    "rcb/dewit": UserRole.DEWIT,
    "rcb/transvision": UserRole.TRANSVISION,
    "support-consultant": UserRole.SUPPORTCONSULTANT,
} as const satisfies Record<ApiDomain, UserRole>;

export function getApiDomain(previewRole: UserRole | null, hasRole: (role: UserRole) => boolean) {
    const userRoles: UserRole[] = allRoles.filter((role) => hasRole(role));
    const isAdmin = userRoles.includes(UserRole.ADMIN);
    const apiDomain: ApiDomain | null =
        userRoles
            .map((r) => getPrefixFromRole(r))
            .filter(Boolean)
            .at(0) ?? null;
    return isAdmin && previewRole ? getPrefixFromRole(previewRole) : apiDomain;
}

const apiDomainsRegex = new RegExp(`^/(${apiDomains.join("|")})`);
const endpointExceptions = ["infra", "debug", "config", "beheer"];
const endpointExceptionsRegex = new RegExp(`^/(${endpointExceptions.join("|")})`);

export const withApiDomain = (apiDomain: ApiDomain | null) => (url: string) => {
    const maybeForwardSlash = url.startsWith("/") ? "" : "/";
    if (apiDomain !== null && !endpointExceptionsRegex.test(url) && !apiDomainsRegex.test(url)) {
        return `/${apiDomain}${maybeForwardSlash}${url}`;
    }
    return url;
};

function getPrefixFromRole(role: UserRole | null): ApiDomain | null {
    switch (role) {
        case UserRole.REGIEVOERDER:
            return "regievoerder";
        case UserRole.KLANTENSERVICE:
            return "klantenservice";
        case UserRole.SUPPORTCONSULTANT:
            return "support-consultant";
        case UserRole.ALLEENLEZEN:
            return "alleen-lezen";
        case UserRole.DEWIT:
            return "rcb/dewit";
        case UserRole.TRANSVISION:
            return "rcb/transvision";
        case UserRole.ADMIN:
        case UserRole.VRIJWILLIGER:
        default:
            console.log(`Prefix from user role could not be determined: ${role}`);
            return null;
    }
}
